import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ExternalLink, X } from 'lucide-react'
import { Avatar, Button } from '../../../../components'
import { FormTextarea } from '../../../../components/form/FormTextarea'
import {
  calculateDays,
  formatDateDisplay,
} from '../constants/hrmsLeaveRequests.constants'
import type { StaffLeaveRequest } from '../types/hrmsLeaveRequests.types'
import { mapLeaveApplicationToLeaveRequest } from '../api/hrmsLeaveRequests.api'
import { useLeaveRequestDetail } from '../hooks/useLeaveRequests'
import { getApiErrorMessage } from '../../../../shared/utils/errorUtils'
import { RejectLeaveModal } from './RejectLeaveModal'

interface LeaveRequestDrawerProps {
  requestId?: string | null
  request?: StaffLeaveRequest | null
  mode?: 'review' | 'view'
  onClose: () => void
  onApprove?: (id: string, remarks?: string) => void
  onReject?: (id: string, reason: string, remarks?: string) => void
  isSubmitting?: boolean
}

export function LeaveRequestDrawer({
  requestId,
  request: propRequest,
  mode,
  onClose,
  onApprove,
  onReject,
  isSubmitting = false,
}: LeaveRequestDrawerProps) {
  const activeId = requestId || propRequest?.id || null

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
    refetch: refetchDetail,
  } = useLeaveRequestDetail(activeId)

  const [remarks, setRemarks] = useState<string>('')
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false)

  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const currentRequest: StaffLeaveRequest | null = detailData
    ? mapLeaveApplicationToLeaveRequest(detailData)
    : propRequest || null

  useEffect(() => {
    if (!activeId) return

    setRemarks(currentRequest?.remarks || '')
    setIsRejectModalOpen(false)

    previousFocusRef.current = document.activeElement as HTMLElement
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const timer = setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 50)

    return () => {
      document.body.style.overflow = originalOverflow
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
      clearTimeout(timer)
    }
  }, [activeId, currentRequest?.remarks])

  useEffect(() => {
    if (!activeId) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isRejectModalOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeId, isRejectModalOpen, onClose])

  if (!activeId && !propRequest) return null

  const rawStatus = (detailData?.status || currentRequest?.status || '').toUpperCase()
  const isPending = rawStatus === 'PENDING'
  const isReadOnly = mode === 'view' || !isPending

  const daysCount = currentRequest ? calculateDays(currentRequest.from, currentRequest.to) : '—'
  const fromDisplay = currentRequest ? formatDateDisplay(currentRequest.from) : '—'
  const toDisplay = currentRequest ? formatDateDisplay(currentRequest.to) : '—'
  const durationText = `${daysCount} (${fromDisplay} – ${toDisplay})`
  const attachmentUrl = detailData?.attachmentUrl || currentRequest?.attachmentUrl

  return createPortal(
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-request-drawer-title"
        className="fixed inset-0 z-[100] overflow-x-hidden font-sans"
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity z-[100]"
          onClick={isSubmitting ? undefined : onClose}
        />

        {/* Panel */}
        <div className="fixed top-0 right-0 z-[101] h-screen w-full max-w-[420px] bg-white shadow-xl rounded-l-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 z-10 px-5 py-4 flex items-center justify-between shrink-0">
            <div className="min-w-0">
              <h2 id="leave-request-drawer-title" className="text-lg font-bold text-slate-900 truncate">
                Leave Request
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Review request &amp; faculty leave quota
              </p>
            </div>

            <Button
              ref={closeButtonRef}
              variant="secondary"
              size="sm"
              aria-label="Close"
              disabled={isSubmitting}
              icon={<X className="h-4 w-4" />}
              onClick={onClose}
              className="h-8 w-8 p-0 flex items-center justify-center border-none bg-transparent hover:bg-slate-100 text-slate-500 rounded-lg shrink-0"
            />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {isDetailLoading ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-3 text-slate-500">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#146971] border-t-transparent" />
                <p className="text-sm font-medium">Loading leave request details...</p>
              </div>
            ) : isDetailError ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4 text-center">
                <div className="rounded-full bg-red-50 p-3 text-red-600">
                  <AlertTriangle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Failed to load details</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-[280px]">
                    {getApiErrorMessage(detailError, 'Could not retrieve leave application details.')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => refetchDetail()}
                  className="text-[#146971] font-semibold text-xs border-slate-200"
                >
                  Retry
                </Button>
              </div>
            ) : currentRequest ? (
              <>
                {/* Employee Card */}
                <div className="rounded-xl bg-gray-100 p-3 flex items-center justify-between gap-3 min-w-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar
                      name={currentRequest.name}
                      size="sm"
                      className="bg-[#146971] text-white shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 text-sm truncate">{currentRequest.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {currentRequest.employeeId} • {currentRequest.department}
                      </div>
                    </div>
                  </div>

                  {/* Status Pill */}
                  {currentRequest.status === 'Approved' ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 border border-green-200 shrink-0">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      Approved
                    </span>
                  ) : currentRequest.status === 'Rejected' ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 border border-red-200 shrink-0">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      Rejected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 border border-amber-200 shrink-0">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      Pending Approval
                    </span>
                  )}
                </div>

                {/* Details Card */}
                <div className="rounded-xl bg-gray-100 p-4 space-y-4">
                  {/* Row a: Leave Type & Available Balance */}
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        LEAVE TYPE
                      </div>
                      <div className="font-bold text-slate-900 text-sm mt-0.5">
                        {currentRequest.leaveType}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        AVAILABLE BALANCE
                      </div>
                      <div className="font-bold text-[#146971] text-sm mt-0.5">
                        8.0 days remaining
                      </div>
                    </div>
                  </div>

                  {/* Row b: Duration */}
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      DURATION
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">
                      {durationText}
                    </div>
                  </div>

                  {/* Row c: Stated Reason */}
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      STATED REASON
                    </div>
                    <div className="mt-1 rounded-lg bg-white p-3 text-sm text-slate-700 font-normal border border-slate-200/60">
                      {currentRequest.reason || 'Personal emergency requiring leave of absence.'}
                    </div>
                  </div>

                  {/* Row d: Quota Impact */}
                  <div className="rounded-lg bg-gray-50 px-3 py-2 flex items-center justify-between border border-slate-200/50">
                    <span className="text-xs text-slate-500 font-medium">Quota Impact</span>
                    <span className="text-xs font-bold text-[#146971]">
                      {currentRequest.leaveType}: 8 rem. / 12 alloc.
                    </span>
                  </div>

                  {/* Row: Attachment (if available) */}
                  {attachmentUrl ? (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        ATTACHMENT
                      </div>
                      <a
                        href={attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2 text-xs font-medium text-[#146971] hover:bg-slate-50 transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View Attached Document
                      </a>
                    </div>
                  ) : null}

                  {/* Row e: Administrative Remarks */}
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      ADMINISTRATIVE REMARKS
                    </div>
                    {isReadOnly ? (
                      <div className="rounded-lg bg-white p-3 text-sm text-slate-700 font-normal border border-slate-200/60">
                        {currentRequest.remarks || remarks || <span className="text-slate-400 italic">No remarks added</span>}
                      </div>
                    ) : (
                      <FormTextarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="Optional notes for institutional audit log..."
                        className="!rounded-lg !border-slate-200 bg-white p-3 text-sm text-slate-700 min-h-[80px] resize-none placeholder:text-slate-400 focus:!border-[#146971]"
                        rows={3}
                      />
                    )}
                  </div>

                  {/* Row f: Rejection Reason (only for Rejected status) */}
                  {currentRequest.status === 'Rejected' && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        REJECTION REASON
                      </div>
                      <div className="rounded-lg bg-white p-3 text-sm text-red-700 font-medium border border-red-200/60">
                        {currentRequest.rejectionReason || 'No rejection reason recorded.'}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
            {isReadOnly ? (
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
                className="!h-10 !px-5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-semibold shadow-none border-none cursor-pointer"
              >
                Close
              </Button>
            ) : currentRequest ? (
              <>
                <Button
                  variant="secondary"
                  disabled={isSubmitting}
                  onClick={() => setIsRejectModalOpen(true)}
                  className="!h-10 !px-4 bg-red-50 hover:bg-red-100 text-red-700 border-none rounded-lg text-sm font-semibold shadow-none cursor-pointer disabled:opacity-50"
                >
                  Reject
                </Button>

                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  onClick={() => onApprove?.(currentRequest.id, remarks)}
                  className="!h-10 !px-4 bg-[#146971] hover:bg-[#0f565d] text-white rounded-lg text-sm font-semibold shadow-none border-none cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Approving...' : 'Approve Leave'}
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Reject Leave Request Modal */}
      {currentRequest ? (
        <RejectLeaveModal
          isOpen={isRejectModalOpen}
          request={currentRequest}
          isSubmitting={isSubmitting}
          onClose={() => setIsRejectModalOpen(false)}
          onConfirmReject={(reqId, reason) => {
            setIsRejectModalOpen(false)
            onReject?.(reqId, reason, remarks)
          }}
        />
      ) : null}
    </>,
    document.body
  )
}

